import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AutoField } from "@/components/common/AutoField";
import { ActivityFeed } from "@/components/common/ActivityFeed";
import { DocumentsPanel } from "@/components/common/DocumentsPanel";
import { Spinner } from "@/components/common/Common";
import { DaysRemaining, StatusBadge } from "@/components/common/StatusBadge";
import { ComposeEmailDialog } from "@/components/email/ComposeEmailDialog";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate, fmtDateTime, formatError } from "@/lib/api";
import { AU_STATES, EMPLOYEE_TYPES, opts } from "@/lib/constants";
import { BankAccountsEditor } from "./BankAccountsEditor";

function Details({ p, save, readOnly }) {
  const f = (field, label, type = "text", extra = {}) => <AutoField field={field} label={label} type={type} value={p[field]} onSave={save} readOnly={readOnly} {...extra} />;
  const a = (field, label, type = "text", extra = {}) => (
    <AutoField field={field} label={label} type={type} value={p.address?.[field]} readOnly={readOnly} {...extra}
      onSave={(patch) => save({ address: { ...p.address, ...patch } })} />
  );
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid="contractor-details">
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {f("first_name", "First name")}{f("last_name", "Last name")}{f("preferred_name", "Preferred name")}
        {f("email", "Email")}{f("mobile", "Mobile")}{f("phone", "Phone")}
        {f("date_of_birth", "Date of birth", "date")}{f("employee_type", "Employee type", "select", { options: opts(EMPLOYEE_TYPES) })}{f("active", "Active", "switch")}
        {a("street", "Street")}{a("suburb", "Suburb")}{a("state", "State", "select", { options: AU_STATES.map((s) => ({ value: s, label: s })) })}{a("postcode", "Postcode")}
        {f("notes", "Notes", "textarea", { className: "sm:col-span-2 xl:col-span-3" })}
      </div>
      <p className="text-[11px] text-slate-400 mt-4" data-testid="contractor-last-changed">Last changed by {p.updated_by || "—"} · {fmtDateTime(p.updated_at)}</p>
    </div>
  );
}

function ContractsHistory({ personId }) {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/contracts", { params: { contractor_id: personId, sort: "start_date", direction: -1, page_size: 100 } }).then((r) => setRows(r.data.items)); }, [personId]);
  return (
    <div className="space-y-3" data-testid="contractor-contracts">
      {canWrite && <Button size="sm" onClick={() => navigate(`/contracts/new?contractor_id=${personId}`)} data-testid="contractor-new-contract-button"><Plus className="h-4 w-4 mr-1" />New contract</Button>}
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100">
        {rows.map((c) => (
          <Link key={c.id} to={`/contracts/${c.id}`} className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-blue-50/40" data-testid={`contractor-contract-${c.id}`}>
            <span className="font-mono text-xs font-semibold">{c.employment_id}</span>
            <StatusBadge status={c.status} />
            <span className="text-sm text-slate-700">{c.recruitment_company_name}</span>
            <span className="text-xs text-slate-500 ml-auto">{fmtDate(c.start_date)} – {fmtDate(c.end_date)} · <DaysRemaining days={c.days_remaining} status={c.status} /> days</span>
          </Link>
        ))}
        {!rows.length && <p className="px-4 py-6 text-center text-sm text-slate-400">No contracts yet</p>}
      </div>
    </div>
  );
}

export default function ContractorProfile() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const { canWrite } = useAuth();
  const [p, setP] = useState(null);
  const [composing, setComposing] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const load = useCallback(() => api.get(`/people/${id}`).then((r) => setP(r.data)).catch((e) => toast.error(formatError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const save = async (patch) => { const { data } = await api.put(`/people/${id}`, patch); setP(data); setReloadKey((k) => k + 1); };

  if (!p) return <div className="flex justify-center p-12"><Spinner /></div>;
  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-6 animate-fade-up" data-testid="contractor-header">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight" data-testid="contractor-name">{p.first_name} {p.last_name}</h1>
          <p className="text-sm text-slate-500 mt-1">Preferred name: {p.preferred_name || p.first_name} · {p.email || "no email"} · {p.mobile || "no mobile"}</p>
        </div>
        <div className="flex gap-2">
          {p.mobile && <Button variant="outline" asChild><a href={`tel:${p.mobile}`} data-testid="contractor-call-button"><Phone className="h-4 w-4 mr-1" />Call</a></Button>}
          {canWrite && p.email && <Button variant="outline" onClick={() => setComposing(true)} data-testid="contractor-email-button"><Mail className="h-4 w-4 mr-1" />Email</Button>}
        </div>
      </div>
      <Tabs defaultValue={params.get("tab") || "details"}>
        <TabsList className="bg-white border border-slate-200 flex-wrap h-auto mb-5">
          <TabsTrigger value="details" data-testid="profile-tab-details">Details</TabsTrigger>
          <TabsTrigger value="bank" data-testid="profile-tab-bank">Bank accounts</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="profile-tab-contracts">Contracts</TabsTrigger>
          <TabsTrigger value="activity" data-testid="profile-tab-activity">Activity</TabsTrigger>
          <TabsTrigger value="documents" data-testid="profile-tab-documents">Documents</TabsTrigger>
        </TabsList>
        <TabsContent value="details"><Details p={p} save={save} readOnly={!canWrite} /></TabsContent>
        <TabsContent value="bank"><BankAccountsEditor personId={id} /></TabsContent>
        <TabsContent value="contracts"><ContractsHistory personId={id} /></TabsContent>
        <TabsContent value="activity"><ActivityFeed url={`/people/${id}/activity`} personId={id} reloadKey={reloadKey} /></TabsContent>
        <TabsContent value="documents"><DocumentsPanel personId={id} /></TabsContent>
      </Tabs>
      {composing && <ComposeEmailDialog draft={{ to: [p.email], subject: "", body: `Hi ${p.preferred_name || p.first_name},\n\n` }} personId={id} onClose={() => setComposing(false)} onSent={() => setReloadKey((k) => k + 1)} />}
    </div>
  );
}
